import { Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Tip } from "@/components/ui/overlays";
import { keys } from "@/hooks/queries";
import { api, queryClient, type DocumentOut } from "@/lib/api";
import { notifyError } from "@/lib/errors";
import { cn } from "@/lib/utils";

import {
  MAX_PAPER_SPACING,
  MIN_PAPER_SPACING,
  PAPER_UNIT,
  normalizeSpacing,
  paperBackground,
  type PaperStyle,
} from "./paper";

/** The paper a note is written on: its colour, its ruling, and how far apart the
 * rules sit.
 *
 * Everything here is applied LOCALLY the moment you touch it — the surfaces read
 * the note's paper from the document query, so the control writes the new value
 * straight into the cache and the canvas repaints on the next frame from a
 * single CSS write. The PATCH follows behind, debounced, and only its FAILURE is
 * ever surfaced. Waiting on a round trip to see a colour change would make
 * choosing paper feel like filing a request.
 */

const BACKGROUNDS = [
  { value: "#ffffff", label: "White" },
  { value: "#fffef7", label: "Cream" },
  { value: "#f3f7ff", label: "Cool" },
  { value: "#f6fff2", label: "Mint" },
  { value: "#fdf2f8", label: "Blush" },
  { value: "#1c1f19", label: "Dark" },
];

const STYLES: { value: PaperStyle; label: string }[] = [
  { value: "blank", label: "Blank" },
  { value: "ruled", label: "Ruled" },
  { value: "grid", label: "Grid" },
  { value: "dots", label: "Dots" },
];

/** How long to sit on a change before writing it. A spacing slider emits a value
 * per pixel of travel; one PATCH per drag is plenty. */
const WRITE_DELAY_MS = 350;

export function PaperControl({ doc }: { doc: DocumentOut }) {
  const background = doc.canvas_background ?? "#ffffff";
  const paper = (doc.canvas_paper as PaperStyle | null) ?? "blank";
  const spacing = normalizeSpacing(doc.canvas_paper_spacing);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<Record<string, unknown>>({});
  const [saved, setSaved] = useState(false);

  useEffect(
    () => () => {
      // A change made and then dismissed still has to land.
      if (timer.current) {
        clearTimeout(timer.current);
        void flush(doc.id, pending.current);
      }
    },
    [doc.id],
  );

  /** Apply now, persist shortly. */
  const change = (patch: { background?: string; paper?: PaperStyle; spacing?: number }) => {
    // 1. Local, instant: the surfaces render from this cache entry.
    const apply = (d: DocumentOut | undefined) =>
      d && {
        ...d,
        canvas_background: patch.background ?? d.canvas_background,
        canvas_paper: patch.paper ?? d.canvas_paper,
        canvas_paper_spacing: patch.spacing ?? d.canvas_paper_spacing,
      };
    queryClient.setQueryData<DocumentOut>(keys.document(doc.id), apply);
    queryClient.setQueryData<{ document: DocumentOut }>(keys.reader(doc.id), (r) =>
      r ? { ...r, document: apply(r.document)! } : r,
    );
    // 2. Coalesced write.
    pending.current = { ...pending.current, ...patch };
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const body = pending.current;
      pending.current = {};
      timer.current = null;
      void flush(doc.id, body).then((ok) => {
        if (!ok) return;
        setSaved(true);
        setTimeout(() => setSaved(false), 1200);
      });
    }, WRITE_DELAY_MS);
  };

  const swatchPreview = (bg: string) => {
    const pattern = paperBackground(paper, PAPER_UNIT * spacing * 0.22, 1);
    return { backgroundColor: bg, ...(pattern ?? {}) };
  };

  return (
    <div className="w-60 space-y-3">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Paper
        </span>
        {/* Quiet confirmation that the (already-applied) change reached the
            server — the change itself was never in doubt. */}
        <span
          className={cn(
            "flex items-center gap-1 text-[10px] text-muted-foreground transition-opacity",
            saved ? "opacity-100" : "opacity-0",
          )}
          aria-live="polite"
        >
          <Check className="h-3 w-3" /> saved
        </span>
      </div>

      <div className="space-y-1.5">
        <span className="text-[11px] text-muted-foreground">Background</span>
        <div className="flex flex-wrap items-center gap-1.5">
          {BACKGROUNDS.map((b) => (
            <Tip key={b.value} label={b.label}>
              <button
                type="button"
                aria-label={`Paper background ${b.label}`}
                aria-pressed={background.toLowerCase() === b.value}
                onClick={() => change({ background: b.value })}
                className={cn(
                  "h-6 w-6 rounded-full border-2 transition-transform",
                  background.toLowerCase() === b.value
                    ? "border-primary"
                    : "border-border hover:scale-110",
                )}
                style={{ background: b.value }}
              />
            </Tip>
          ))}
          <Tip label="Any colour">
            <input
              type="color"
              value={/^#[0-9a-f]{6}$/i.test(background) ? background : "#ffffff"}
              onChange={(e) => change({ background: e.target.value })}
              aria-label="Paper background colour"
              className="h-6 w-6 shrink-0 cursor-pointer rounded border border-border bg-transparent p-0"
            />
          </Tip>
        </div>
      </div>

      <div className="space-y-1.5">
        <span className="text-[11px] text-muted-foreground">Lines</span>
        <div className="grid grid-cols-4 gap-1">
          {STYLES.map((s) => (
            <button
              key={s.value}
              type="button"
              aria-label={`Paper ${s.label}`}
              aria-pressed={paper === s.value}
              onClick={() => change({ paper: s.value })}
              className={cn(
                "flex flex-col items-center gap-1 rounded-md p-1 text-[10px] transition-colors",
                paper === s.value
                  ? "bg-primary/15 text-primary"
                  : "text-muted-foreground hover:bg-accent",
              )}
            >
              {/* A live preview of the actual pattern on the actual colour, so
                  the choice reads at a glance rather than from its name. */}
              <span
                aria-hidden
                className="h-7 w-full rounded-[3px] border border-border"
                style={{
                  backgroundColor: background,
                  ...(paperBackground(s.value, Math.max(5, PAPER_UNIT * spacing * 0.18), 1) ?? {}),
                }}
              />
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className={cn("space-y-1.5", paper === "blank" && "opacity-40")}>
        <label className="flex items-center justify-between text-[11px] text-muted-foreground">
          Spacing
          <span className="font-mono tabular-nums">{Math.round(spacing * 100)}%</span>
        </label>
        <input
          type="range"
          min={MIN_PAPER_SPACING}
          max={MAX_PAPER_SPACING}
          step={0.05}
          value={spacing}
          disabled={paper === "blank"}
          aria-label="Paper line spacing"
          onChange={(e) => change({ spacing: Number(e.target.value) })}
          className="w-full accent-[hsl(var(--primary))]"
        />
        <span
          aria-hidden
          className="block h-8 w-full rounded-[3px] border border-border"
          style={swatchPreview(background)}
        />
      </div>
    </div>
  );
}

/** Persist a paper patch. Returns whether it landed; a failure is surfaced (the
 * user is looking at a change that now only exists on their screen). */
async function flush(docId: string, body: Record<string, unknown>): Promise<boolean> {
  if (!Object.keys(body).length) return false;
  try {
    const doc = await api.request<DocumentOut>("PATCH", `/api/documents/${docId}/paper`, body);
    // Adopt the server's copy so the cache can't drift from what was stored.
    queryClient.setQueryData<DocumentOut>(keys.document(docId), doc);
    void queryClient.invalidateQueries({ queryKey: keys.documents });
    return true;
  } catch (e) {
    notifyError(e);
    void queryClient.invalidateQueries({ queryKey: keys.document(docId) });
    void queryClient.invalidateQueries({ queryKey: keys.reader(docId) });
    return false;
  }
}
